/**
 * A horizontal swipe, the way a thumb actually makes one.
 *
 * Three things this does that a naive touchstart/touchend pair does not:
 *
 * 1. It decides while the finger is still down. A gesture that is judged only
 *    on `touchend` is lost whenever the browser never delivers one, and Chrome
 *    on Android does exactly that: as soon as it hands the gesture to something
 *    else it fires `touchcancel` instead. The turn happens the moment the
 *    finger has travelled far enough, which is also when it feels right.
 * 2. It treats `touchcancel` as an end, not as an erasure.
 * 3. It only refuses to turn when the finger has somewhere left to pan IN THAT
 *    DIRECTION. Asking whether the frame is scrollable at all, as the first
 *    version did, killed the swipe on every page wider than its frame by a
 *    single rounded pixel.
 *
 * A trackpad is fed in too, because on a laptop a two-finger horizontal push is
 * the same intention and arrives as a wheel event, never as a touch.
 */

import { useCallback, useRef } from "react";
import type { RefObject, TouchEvent, WheelEvent } from "react";

/** Far enough to be a swipe and not a shaky tap. Below a thumb's width. */
const DISTANCE = 40;
/** One notch of trackpad push. Two fingers cover this in a short flick. */
const WHEEL = 90;
/** A wheel that goes quiet for this long starts its count again. */
const WHEEL_GAP = 400;
/** A tap that lands right after a turn belongs to the turn, not to the page. */
const AFTERGLOW = 500;

export interface SwipeHandlers {
  onTouchStart: (event: TouchEvent) => void;
  onTouchMove: (event: TouchEvent) => void;
  onTouchEnd: (event: TouchEvent) => void;
  onTouchCancel: (event: TouchEvent) => void;
  onWheel: (event: WheelEvent) => void;
}

export function useSwipe(
  /** +1 for a page forward (finger travelling left), -1 for back. */
  turn: (direction: 1 | -1) => void,
  /** The scroller, when the content can be panned inside it. */
  frame?: RefObject<HTMLElement | null>,
): { handlers: SwipeHandlers; justSwiped: () => boolean } {
  const drag = useRef<{
    x: number;
    y: number;
    pan: number;
    fired: boolean;
  } | null>(null);
  const wheel = useRef({ sum: 0, at: 0 });
  const firedAt = useRef(0);

  const fire = useCallback(
    (direction: 1 | -1) => {
      firedAt.current = performance.now();
      turn(direction);
    },
    [turn],
  );

  const consider = useCallback(
    (x: number, y: number) => {
      const held = drag.current;
      if (!held || held.fired) return;
      const dx = x - held.x;
      const dy = y - held.y;
      // Mostly sideways. Not "1.4 times sideways": a thumb arcs, and a real
      // swipe on a phone routinely drifts a third of its length downward.
      if (Math.abs(dx) < DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;
      const box = frame?.current;
      if (box && held.pan > 4) {
        const room = dx < 0 ? held.pan - box.scrollLeft : box.scrollLeft;
        if (room > 4) return; // zoomed in, and this direction still has page to show
      }
      held.fired = true;
      fire(dx < 0 ? 1 : -1);
    },
    [fire, frame],
  );

  const handlers: SwipeHandlers = {
    onTouchStart: useCallback(
      (event: TouchEvent) => {
        if (event.touches.length !== 1) {
          drag.current = null; // pinching, not swiping
          return;
        }
        const box = frame?.current;
        drag.current = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
          pan: box ? box.scrollWidth - box.clientWidth : 0,
          fired: false,
        };
      },
      [frame],
    ),
    onTouchMove: useCallback(
      (event: TouchEvent) => {
        const point = event.touches[0];
        if (point) consider(point.clientX, point.clientY);
      },
      [consider],
    ),
    onTouchEnd: useCallback(
      (event: TouchEvent) => {
        const point = event.changedTouches[0];
        if (point) consider(point.clientX, point.clientY);
        drag.current = null;
      },
      [consider],
    ),
    onTouchCancel: useCallback(
      (event: TouchEvent) => {
        const point = event.changedTouches[0];
        if (point) consider(point.clientX, point.clientY);
        drag.current = null;
      },
      [consider],
    ),
    onWheel: useCallback(
      (event: WheelEvent) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        const now = performance.now();
        const count = wheel.current;
        if (
          now - count.at > WHEEL_GAP ||
          Math.sign(event.deltaX) !== Math.sign(count.sum)
        ) {
          count.sum = 0;
        }
        count.at = now;
        count.sum += event.deltaX;
        if (Math.abs(count.sum) < WHEEL) return;
        const direction = count.sum > 0 ? 1 : -1;
        count.sum = 0;
        fire(direction);
      },
      [fire],
    ),
  };

  return {
    handlers,
    justSwiped: useCallback(
      () => performance.now() - firedAt.current < AFTERGLOW,
      [],
    ),
  };
}
